/**
 * Сравнение двух стилей бок о бок — docs/release-2026-09-19.md §5.4. Страница
 * `/b/:brand/compare/:requestId`: сюда ведут готовая заявка сравнения в «Запустить генерацию» и
 * кнопка «Сравнить» на экране черновика из пары.
 *
 * ЧТО НА ЭКРАНЕ. Два превью ТЕКУЩИХ версий обоих черновиков — тем же `MessagePreview`, что и
 * экран черновика, в режиме «как выйдет в канал»: сравнивают то, что увидит подписчик, а не
 * разметку. Над каждым — буква половины и стиль, пилюля статуса и «Открыть черновик»; решения
 * по постам принимаются там, в черновике, а не здесь: здесь выбирают глазами.
 *
 * ЗАЯВКА ИЩЕТСЯ В СПИСКЕ, А НЕ ОТДЕЛЬНЫМ МАРШРУТОМ. Маршрута «одна заявка» у интерфейса нет, и
 * заводить его ради страницы, которую открывают из того же списка, незачем: последних 200
 * заявок бренда хватает с запасом. Не нашлась — так и сказано, а не пустой экран.
 *
 * НЕ ВЫШЕЛ ВТОРОЙ — ПОЛОВИНА ГОВОРИТ ПОЧЕМУ. Заявка сравнения закрывается `done` с первым
 * черновиком и причиной в `outcome_detail`, если второй не получился (§5.4): эта причина стоит
 * на месте второго превью, а не теряется в списке заявок.
 *
 * Пока заявка в работе, страница сама перечитывает её раз в 5 секунд — её держат открытой
 * рядом, пока пишутся оба черновика.
 *
 * ОДИН НЕУДАЧНЫЙ ОПРОС — НЕ ПОВОД ТЕРЯТЬ ПАРУ (ревью партии, U-B3). Сбой перечитывания
 * оставляет последнюю удачную заявку на экране с плашкой «не обновилась» (`useLastGood`), и
 * опрос продолжается, пока последняя известная заявка в очереди или в работе: следующий
 * удачный ответ снимает плашку сам. Раньше один сбой менял пару превью на ошибку во всю
 * страницу и останавливал опрос — страница, которую держат открытой рядом, замирала навсегда.
 */

import { type JSX, useEffect, useMemo } from 'react';
import { Link } from 'wouter';
import {
  DraftWithVersionsSchema,
  type GenerationRequest,
  type GenerationRequestState,
  GenerationRequestsResponseSchema,
  type RenderModel,
  RenderModelSchema,
} from '../api/types.ts';
import { useLastGood } from '../api/useLastGood.ts';
import { useResource } from '../api/useResource.ts';
import { shortMsk } from '../components/format.ts';
import { Shell } from '../components/Shell.tsx';
import { StatusPill } from '../components/StatusPill.tsx';
import { MessagePreview } from '../preview/MessagePreview.tsx';
import styles from './ComparePage.module.css';

/** Сколько заявок бренда просматривается в поисках нужной — потолок маршрута списка. */
const REQUESTS_WINDOW = 200;

/** Опрос, пока заявка в очереди или в работе. */
const POLL_MS = 5000;

/** Имена и пилюли состояний — те же, что в списке заявок диалога (docs/ui-design.md §2.1). */
const STATE_LABEL: Readonly<Record<GenerationRequestState, string>> = {
  queued: 'в очереди',
  taken: 'в работе',
  done: 'готово',
  failed: 'не вышло',
  cancelled: 'отменена',
};

const STATE_PILL: Readonly<Record<GenerationRequestState, string>> = {
  queued: 'st-wait',
  taken: 'st-sched',
  done: 'st-done',
  failed: 'st-err',
  cancelled: 'st-hold',
};

/**
 * Причина исхода словами сервиса, вписанная в свою фразу: точка в конце у сервиса бывает, и
 * «…не прошёл гейт..» с двумя точками читалось бы опиской.
 */
const reasonOf = (detail: string | null): string =>
  detail === null || detail.trim() === ''
    ? 'причина не передана'
    : detail.trim().replace(/\.+$/, '');

export function ComparePage({
  brand,
  requestId,
}: {
  brand: string;
  requestId: string;
}): JSX.Element {
  return (
    <Shell brand={brand} active={null}>
      <CompareView brand={brand} requestId={requestId} />
    </Shell>
  );
}

function CompareView({
  brand,
  requestId,
}: {
  readonly brand: string;
  readonly requestId: string;
}): JSX.Element {
  const listPath = `/api/v1/generation-requests?brand=${encodeURIComponent(brand)}&limit=${String(REQUESTS_WINDOW)}`;
  const list = useResource<unknown>(listPath);
  const fresh = useMemo(() => {
    if (list.data === undefined) return null;
    const r = GenerationRequestsResponseSchema.safeParse(list.data);
    return r.success ? r.data : null;
  }, [list.data]);
  // Последний удачный список: сбой опроса его не стирает (см. шапку файла).
  const shown = useLastGood(listPath, list, fresh);
  const parsed = shown.value;
  const request = parsed?.requests.find((r) => r.request_id === requestId) ?? null;
  const active = request !== null && (request.state === 'queued' || request.state === 'taken');

  // Самопланирующийся таймер от последнего ОТВЕТА — удачного или нет, а не интервал:
  // медленный ответ не накладывается на следующий запрос. Каждый ответ — новый объект данных
  // или новая ошибка — ставит новый таймер; пока запрос в пути, таймера нет. Раньше таймер
  // ставил только новый `list.data`, и первый же сбой (данные сброшены, ошибка вместо них)
  // останавливал опрос навсегда. Одного `loading` мало: быстрый ответ React склеивает с его
  // началом в одну отрисовку, и `loading` для эффекта не меняется вовсе.
  const { refetch, loading, data: answered, error: refused } = list;
  useEffect(() => {
    if (!active || loading || (answered === undefined && refused === undefined)) return;
    const t = window.setTimeout(refetch, POLL_MS);
    return () => {
      window.clearTimeout(t);
    };
  }, [active, loading, answered, refused, refetch]);

  const title = <h1 className="h1">Сравнение стилей</h1>;

  if (parsed === null && shown.failure !== null) {
    return (
      <div className={styles['page']}>
        {title}
        <div className={`val val-err ${styles['notice']}`} role="alert">
          <span className={styles['grow']}>
            {`Заявки не загрузились: ${reasonOf(shown.failure)}.`}
          </span>
          <button type="button" className="btn btn-ghost btn-sm" onClick={refetch}>
            Повторить
          </button>
        </div>
      </div>
    );
  }
  // Показано прежнее: плашка небольшая и не прячет пару — сравнивать можно и по нему.
  const stale =
    shown.failure === null ? null : (
      <div className={`val val-warn ${styles['notice']}`} role="status">
        <span className={styles['grow']}>
          {`Заявка не обновилась: ${reasonOf(shown.failure)}. На экране — последнее загруженное.`}
        </span>
        <button type="button" className="btn btn-ghost btn-sm" onClick={refetch}>
          Повторить
        </button>
      </div>
    );
  if (parsed === null) {
    return (
      <div className={styles['page']}>
        {title}
        <span className="micro">Загружаем сравнение…</span>
      </div>
    );
  }
  if (request === null) {
    return (
      <div className={styles['page']}>
        {title}
        {stale}
        <div className="val val-warn" role="alert">
          <span>
            {`Заявка сравнения не найдена среди последних ${String(REQUESTS_WINDOW)} заявок бренда.`}
          </span>
        </div>
        <Link href={`/b/${encodeURIComponent(brand)}/backlog`} className="btn btn-ghost btn-sm">
          В бэклог
        </Link>
      </div>
    );
  }
  if (request.compare_style_version_id === null) {
    return (
      <div className={styles['page']}>
        {title}
        {stale}
        <div className="val val-info" role="status">
          <span>Эта заявка писала один черновик — сравнивать не с чем.</span>
        </div>
        {request.draft_id === null ? null : (
          <Link
            href={`/b/${encodeURIComponent(brand)}/draft/${encodeURIComponent(request.draft_id)}`}
            className="btn btn-ghost btn-sm"
          >
            Открыть черновик
          </Link>
        )}
      </div>
    );
  }

  const nameA = request.style_name ?? 'по умолчанию';
  const nameB = request.compare_style_name ?? '—';

  return (
    <div className={styles['page']}>
      <div className={styles['head']}>
        {title}
        <div className={styles['headMeta']}>
          <span className={`pill ${STATE_PILL[request.state]}`}>{STATE_LABEL[request.state]}</span>
          <span className="meta">
            {`${request.requested_by_login} · ${shortMsk(request.created_at)} · ${nameA} / ${nameB}`}
          </span>
        </div>
      </div>
      {stale}

      {request.state === 'failed' || request.state === 'cancelled' ? (
        <div className="val val-err" role="alert">
          <span>
            {request.state === 'cancelled'
              ? 'Заявку отменили до того, как её взял сервис: черновиков нет.'
              : `Прогон не вышел: ${reasonOf(request.outcome_detail)}.`}
          </span>
        </div>
      ) : (
        <div className={styles['sides']}>
          <CompareSide
            brand={brand}
            side="A"
            styleName={nameA}
            draftId={request.draft_id}
            request={request}
          />
          <CompareSide
            brand={brand}
            side="B"
            styleName={nameB}
            draftId={request.compare_draft_id}
            request={request}
          />
        </div>
      )}
    </div>
  );
}

/**
 * Половина пары: черновик и его ТЕКУЩАЯ версия. Стиль на чипе — факт версии
 * (`RenderModel.style`), а если его нет — имя из заявки: заявка хранит выбор человека, версия —
 * то, чем текст написан на самом деле.
 */
function CompareSide({
  brand,
  side,
  styleName,
  draftId,
  request,
}: {
  readonly brand: string;
  readonly side: 'A' | 'B';
  readonly styleName: string;
  readonly draftId: string | null;
  readonly request: GenerationRequest;
}): JSX.Element {
  const draft = useResource<unknown>(
    draftId === null ? null : `/api/v1/drafts/${encodeURIComponent(draftId)}`,
  );
  const head = useMemo(() => {
    if (draft.data === undefined) return null;
    const r = DraftWithVersionsSchema.safeParse(draft.data);
    return r.success ? r.data : null;
  }, [draft.data]);
  // Номер текущей версии — последний в списке, как на экране черновика (`DraftPanel`).
  const versionNo = head?.versions.at(-1)?.version_no ?? null;
  const model = useResource<unknown>(
    draftId === null || versionNo === null
      ? null
      : `/api/v1/drafts/${encodeURIComponent(draftId)}/versions/${String(versionNo)}`,
  );
  const rm: RenderModel | null = useMemo(() => {
    if (model.data === undefined) return null;
    const r = RenderModelSchema.safeParse(model.data);
    return r.success ? r.data : null;
  }, [model.data]);

  const chip = `${side} · ${rm?.style?.name ?? styleName}`;
  const broken =
    draft.error !== undefined ||
    model.error !== undefined ||
    (draft.data !== undefined && head === null) ||
    (model.data !== undefined && rm === null);

  let body: JSX.Element;
  if (draftId === null) {
    body =
      request.state === 'done' ? (
        <div className="val val-err" role="alert">
          <span>{`Черновик ${side} не получился: ${reasonOf(request.outcome_detail)}.`}</span>
        </div>
      ) : (
        <span className="micro">Пишется…</span>
      );
  } else if (broken) {
    const e = draft.error ?? model.error;
    body = (
      <div className="val val-err" role="alert">
        <span>{`Черновик не открылся${e === undefined ? ': ответ сервера не разобран' : `: ${e.message}`}.`}</span>
      </div>
    );
  } else if (rm === null) {
    body = <span className="micro">Загружаем черновик…</span>;
  } else {
    body = (
      <div className="tgzone">
        <MessagePreview
          canonical={rm.canonical}
          mediaRef={rm.media_ref}
          documentRef={rm.document_ref ?? null}
          mode="expected"
          theme="light"
          brand={rm.brand}
        />
      </div>
    );
  }

  return (
    <section className={styles['side']} aria-label={`Черновик ${side}`}>
      <div className={styles['sideHead']}>
        <span className="chip">{chip}</span>
        {rm === null ? null : <StatusPill status={rm.status} />}
        <span className={styles['spacer']} />
        {draftId === null ? null : (
          /* Ссылка — в бренд ЧЕРНОВИКА, когда модель пришла: решения по посту принимаются в его
             бренде (экран черновика и сам переведёт туда, но лишний переход ни к чему). */
          <Link
            href={`/b/${encodeURIComponent(rm?.brand ?? brand)}/draft/${encodeURIComponent(draftId)}`}
            className="btn btn-ghost btn-sm"
          >
            Открыть черновик
          </Link>
        )}
      </div>
      {body}
    </section>
  );
}
